

import React, { useState } from "react";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import { formatPrice } from "../../utils/productPricing";

const CheckoutForm = ({ order, items, onSuccess, disabled }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements || disabled) {
      return;
    }

    setIsProcessing(true);

    try {
      // Get client secret from your API
      const response = await fetch('/api/create-payment-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order, items })
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || `Payment API error (${response.status})`);
      }

      const { client_secret } = payload;
      if (!client_secret) {
        throw new Error('Payment API returned an invalid response');
      }

      // Confirm payment with Stripe
      const { error, paymentIntent } = await stripe.confirmCardPayment(client_secret, {
        payment_method: {
          card: elements.getElement(CardElement)
        }
      });

      if (error) {
        console.error('Payment error:', error);
        alert('Payment failed: ' + error.message);
      } else if (paymentIntent.status === 'succeeded') {
        onSuccess(paymentIntent);
      }
    } catch (error) {
      console.error('Payment failed:', error);
      alert(`Payment processing failed: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-3.5">
      <div>
        <label className="mb-1.5 flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-[0.16em] text-slate-400">
          <span className="text-amber-400/70">
            <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1M5 6h14a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z" />
            </svg>
          </span>
          Card Details
        </label>
        <div className="rounded-xl border border-white/10 bg-white/[0.06] px-3.5 py-3.5 transition hover:border-white/20 focus-within:border-amber-400/70 focus-within:bg-white/[0.08] focus-within:ring-2 focus-within:ring-amber-400/15 sm:px-3 sm:py-3">
          <CardElement
            options={{
              disabled: disabled || isProcessing,
              style: {
                base: {
                  fontSize: "16px",
                  color: "#f1f5f9",
                  fontFamily: "inherit",
                  "::placeholder": {
                    color: "#64748b",
                  },
                  iconColor: "#fbbf24",
                },
                invalid: {
                  color: "#fca5a5",
                  iconColor: "#fca5a5",
                },
              },
            }}
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={!stripe || isProcessing || disabled}
        className="flex h-12 w-full items-center justify-center rounded-xl bg-gradient-to-r from-amber-300 via-amber-400 to-yellow-500 px-4 text-sm font-bold text-slate-950 shadow-lg shadow-amber-500/10 transition hover:brightness-105 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-40 sm:h-11 sm:text-xs"
      >
        {isProcessing ? "Processing..." : `Pay ${formatPrice(order.total)}`}
      </button>
    </form>
  );
};

export default CheckoutForm;
